import * as React from 'react';
import { CircleHelp, CircleOff } from 'lucide-react';
import { Card, CardContent, cn } from '@bliks-io/ui';

interface StatusSuccessorItemProps {
  status: 'UNCLEAR' | 'NONE';
  label: string;
  description?: string;
}

/**
 * StatusSuccessorItem Component
 *
 * Displays a process end status (unclear or none) as a prominent card.
 * Shows appropriate icon, label, and descriptive text with distinctive styling.
 */
export const StatusSuccessorItem = React.forwardRef<HTMLDivElement, StatusSuccessorItemProps>(
  ({ status, label, description }, ref) => {
    const Icon = status === 'UNCLEAR' ? CircleHelp : CircleOff;

    // Different styling for each status
    const statusStyles = {
      UNCLEAR: 'bg-warning-surface border-warning-line',
      NONE: 'bg-surface-subtle border-line'
    };

    const iconStyles = {
      UNCLEAR: 'text-warning',
      NONE: 'text-fg-secondary'
    };

    return (
      <Card ref={ref} className={cn('border-2', statusStyles[status])}>
        <CardContent className="p-4 pr-8">
          <div className="flex items-start gap-3">
            <div
              className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                status === 'UNCLEAR' ? 'bg-warning-surface-strong' : 'bg-muted'
              )}
            >
              <Icon className={cn('h-5 w-5', iconStyles[status])} />
            </div>
            <div className="flex-1 space-y-1">
              <div className="text-base font-semibold">{label}</div>
              {description && <div className="text-sm text-muted-foreground">{description}</div>}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
);

StatusSuccessorItem.displayName = 'StatusSuccessorItem';
